import React, { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Building2, Plus, Trash2, Camera, X, ChevronDown, ChevronRight } from "lucide-react";
import { UNIT_LABEL, computeZoneArea } from "../../lib/calculator";
import RawNumInput from "./RawNumInput";
import { JoineryBlock } from "./JoineryBlock";
import ZoneTemplateBlock from "./ZoneTemplateBlock";
import FacadeSurfaceRow, { DeductionRow } from "./FacadeSurfaceRow";
import FacadeExtraElementBlock from "./FacadeExtraElementBlock";
import ExtraElementsGroupedBlock from "./ExtraElementsGroupedBlock";
import { autoSelectOnFocus } from "../../lib/autoNameSelect";
import { EditableDescription } from "./EditableDescription";

// v6 F42k — Fase 3 Zone-vereenvoudiging: nieuwe buitenzone-presets. Voorgevel/
// Achtergevel/Linkergevel/Rechtergevel verdwijnen uit het menu voor NIEUWE
// zones — legacy zones met die namen blijven werken. Nieuwe categorieën:
// Woning, Chalet, Bijgebouw, Bedrijfsgebouw = gebouw-containers met eigen
// gevelsjabloon; hierin voegt de gebruiker Gevel 1/2/3 als facade_surfaces toe.
// Buitenelementen = zelfstandige zone met facade_extra_elements, auto-gegroepeerd
// op (kind + material). Tuinmuur en Trapwerken/Schrijnwerk blijven ongewijzigd.
export const EXTERIOR_ZONE_PRESETS = [
  { name: "Woning",           zone_type: "exterior_area", zone_kind: "woning" },
  { name: "Chalet",           zone_type: "exterior_area", zone_kind: "chalet" },
  { name: "Bijgebouw",        zone_type: "exterior_area", zone_kind: "bijgebouw" },
  { name: "Bedrijfsgebouw",   zone_type: "exterior_area", zone_kind: "bedrijfsgebouw" },
  { name: "Buitenelementen",  zone_type: "exterior_area", zone_kind: "buitenelementen" },
  { name: "Tuinmuur",         zone_type: "exterior_area" },
  { name: "Binnenschrijnwerk", zone_type: "interior_joinery" },
  { name: "Buitenschrijnwerk", zone_type: "exterior_joinery" },
  { name: "Trapwerken",       zone_type: "stair_zone"    },
];

// v6 F12 — GENERAL_ZONE_PRESETS is verwijderd. Regiewerk → "Extra posten /
// opties" (projectbrede sectie). Decoratief → via template of extras.
// Kamer → "Ruimte toevoegen" (aparte flow).
export const GENERAL_ZONE_PRESETS = [];

const ZONE_TYPE_LABEL = {
  exterior_area:     "Gevel",
  woodwork:          "Houtwerk",
  joinery:           "Schrijnwerk",       // legacy
  interior_joinery:  "Binnenschrijnwerk", // v6 F15
  exterior_joinery:  "Buitenschrijnwerk", // v6 F15
  stair_zone:        "Trap",
  decorative:        "Decoratief",
  general:           "Algemeen",
  hourly_work:       "Regie",
  interior_room:     "Kamer",
};

const ZONE_BADGE = {
  exterior_area:    "bg-[#6B8E6B]/15 text-[#3E5A3E] border border-[#6B8E6B]/30",
  woodwork:         "bg-[#D4A373]/15 text-[#7C5320] border border-[#D4A373]/30",
  joinery:          "bg-[#B07A4F]/15 text-[#7F4124] border border-[#B07A4F]/30",  // legacy
  // v6 F15 — Twee expliciete schrijnwerk-varianten met eigen accentkleuren.
  interior_joinery: "bg-[#D4A373]/18 text-[#7C5320] border border-[#D4A373]/35",
  exterior_joinery: "bg-[#B07A4F]/18 text-[#7F4124] border border-[#B07A4F]/35",
  stair_zone:       "bg-[#7F4124]/12 text-[#7F4124] border border-[#7F4124]/25",
  decorative:       "bg-[#8A6BA0]/15 text-[#5A4470] border border-[#8A6BA0]/30",
  general:          "bg-[#C9C8C2]/30 text-[#4F4F4D] border border-[#C9C8C2]",
  hourly_work:      "bg-[#8A9A86]/15 text-[#3E5A3E] border border-[#8A9A86]/30",
  interior_room:    "bg-[#D96C4A]/12 text-[#B34D2E] border border-[#D96C4A]/25",
};

const fmtEUR = (n) => {
  // v6 F8 — Belgische notatie met duizendtalseparator: € 1.234,56
  try {
    return new Intl.NumberFormat("nl-BE", {
      style: "currency", currency: "EUR",
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(+n || 0).replace(/\u00A0/g, " ");
  } catch { return `€ ${(+n || 0).toFixed(2).replace(".", ",")}`; }
};

export function ExteriorZoneAddButtons({
  onAddZone,
  showGeneralPresets = false,
  collapsed = false,
  onToggleCollapsed = null,
  zoneCount = 0,
  // v6 F41 — Optionele overrides zodat één component meerdere sub-blokken kan
  // renderen (Buitenzones, Werkdomeinen, ...). Bij niets doorgeven → default
  // gedrag (backward compat).
  title = "Werkzone toevoegen",
  subtitle = "Voeg een gevel, bijgebouw, schrijnwerk of trap als afzonderlijk werkpakket toe.",
  titleIcon: TitleIcon = Building2,
  presetFilter = null,           // (preset) => bool
  testidSuffix = "",             // bv. "-buiten" of "-werkdomeinen" voor uniciteit
  countLabel = "zone",           // enkelvoud voor collapse-hint
  countLabelPlural = "zones",
}) {
  const allPresets = showGeneralPresets ? [...EXTERIOR_ZONE_PRESETS, ...GENERAL_ZONE_PRESETS] : EXTERIOR_ZONE_PRESETS;
  const presets = typeof presetFilter === "function" ? allPresets.filter(presetFilter) : allPresets;
  // v6 F13 §5B — Als geen onToggleCollapsed → geen collapse-gedrag (backward compat).
  const isCollapsible = typeof onToggleCollapsed === "function";
  const suffix = testidSuffix ? `-${testidSuffix}` : "";
  return (
    <div className="emma-card !p-4" data-testid={`zone-add-buttons${suffix}`}>
      {isCollapsible ? (
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="w-full flex items-center gap-2 text-left lg:cursor-default"
          data-testid={`zone-add-toggle${suffix}`}
          aria-expanded={collapsed ? "false" : "true"}
        >
          <ChevronDown className={`w-4 h-4 text-[#D96C4A] transition-transform lg:hidden ${collapsed ? "-rotate-90" : ""}`} />
          <TitleIcon className="w-4 h-4 text-[#5D7259]" />
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#5D7259] flex-1">{title}</div>
          {collapsed && zoneCount > 0 && (
            <span className="text-[10px] text-[#A8A7A0] font-normal lg:hidden">
              ({zoneCount} {zoneCount === 1 ? countLabel : countLabelPlural})
            </span>
          )}
        </button>
      ) : (
        <div className="flex items-center gap-2 mb-3">
          <TitleIcon className="w-4 h-4 text-[#5D7259]" />
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#5D7259]">{title}</div>
        </div>
      )}
      <div className={`${isCollapsible && collapsed ? "hidden" : "block"} lg:block ${isCollapsible ? "mt-3" : ""}`}>
        <p className="text-xs text-[#4F4F4D] mb-3">
          {subtitle}
        </p>
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p.name}
              onClick={() => onAddZone(p)}
              className="px-4 h-11 rounded-xl border-2 border-[#E5E2DC] hover:border-[#D96C4A] hover:bg-[#FFF8F2] transition font-medium text-sm"
              data-testid={`add-zone-${p.name}`}
            >
              + {p.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ExteriorZoneFlow({
  zones, measurements, priceCodes, templates = [], partsDefaults = {}, projectVat, calc, onAddZone, onUpdateZone, onRemoveZone,
  onAddMeasurement, onUpdateMeasurement, onRemoveMeasurement,
  onPhotoUpload, onPhotoRemove, onPhotoSetLabel, quoteSaved, showGeneralPresets = false,
  zoneCollapsed = {}, onToggleZoneCollapsed,
  hideAddButtons = false,
  autoMeasureEnabled = false,                   // v14am — master-toggle
  renderAutoMeasureBlock,                       // v14ao — (zone) => ReactNode
}) {
  return (
    <div className="space-y-5" data-testid="exterior-zone-flow">
      {!hideAddButtons && (
        <ExteriorZoneAddButtons onAddZone={onAddZone} showGeneralPresets={showGeneralPresets} />
      )}
      {zones.length === 0 ? (
        !hideAddButtons ? (
          <div className="emma-card text-center py-12 text-[#4F4F4D]">
            Nog geen werkzones toegevoegd. Klik op een knop hierboven om te starten.
          </div>
        ) : null
      ) : (
        zones.map((zone) => (
          <div key={zone.id} data-zone-anchor={zone.id}>
          <WorkZoneCard
            zone={zone}
            measurements={(measurements || []).filter((m) => m.zone_id === zone.id)}
            calc={calc}
            priceCodes={priceCodes}
            templates={templates}
            partsDefaults={partsDefaults}
            projectVat={projectVat}
            onUpdateZone={(patch) => onUpdateZone(zone.id, patch)}
            onRemoveZone={() => onRemoveZone(zone.id)}
            onAddMeasurement={(meas) => onAddMeasurement({ ...meas, zone_id: zone.id })}
            onUpdateMeasurement={onUpdateMeasurement}
            onRemoveMeasurement={onRemoveMeasurement}
            onPhotoUpload={(dataUrl, label) => onPhotoUpload(zone.id, dataUrl, label)}
            onPhotoRemove={(idx) => onPhotoRemove(zone.id, idx)}
            onPhotoSetLabel={(idx, label) => onPhotoSetLabel(zone.id, idx, label)}
            quoteSaved={quoteSaved}
            collapsed={typeof zoneCollapsed[zone.id] === "boolean" ? zoneCollapsed[zone.id] : undefined}
            onToggleCollapse={onToggleZoneCollapsed ? (v) => onToggleZoneCollapsed(zone.id, v) : undefined}
            autoMeasureEnabled={autoMeasureEnabled}
            renderAutoMeasureBlock={renderAutoMeasureBlock}
          />
          </div>
        ))
      )}
    </div>
  );
}

// ---------- WorkZone card with measurements + photos ----------
function WorkZoneCard({
  zone, measurements, calc, priceCodes, templates = [], partsDefaults = {}, projectVat,
  onUpdateZone, onRemoveZone, onAddMeasurement, onUpdateMeasurement, onRemoveMeasurement,
  onPhotoUpload, onPhotoRemove, onPhotoSetLabel, quoteSaved,
  collapsed: collapsedProp, onToggleCollapse,
  autoMeasureEnabled = false, renderAutoMeasureBlock,   // v14ao — zone-level slot
}) {
  const [picking, setPicking] = useState(false);
  // v6 F12b — Collapsible zone: nieuwe zones openen standaard.
  // v6 F12d — Collapse state is nu OPTIONEEL gelift naar parent voor bulk-actions
  // ("Alles open / dicht"). Fallback naar local state wanneer parent geen prop levert
  // (backwards-compat).
  const [localCollapsed, setLocalCollapsed] = useState(false);
  const collapsed = (typeof collapsedProp === "boolean") ? collapsedProp : localCollapsed;
  const setCollapsed = (v) => {
    if (onToggleCollapse) {
      // parent-controlled — call the toggle callback with new value
      const newVal = typeof v === "function" ? v(collapsed) : v;
      onToggleCollapse(newVal);
    } else {
      setLocalCollapsed(v);
    }
  };

  const zoneSubtotal = (calc?.lines || [])
    .filter((ln) => ln.zone_id === zone.id)
    .reduce((s, ln) => s + (+ln.total_excl || 0), 0);

  const activeTemplateName = (() => {
    const tid = zone.zone_template_id || zone.joinery_template_id;
    if (!tid) return null;
    const t = (templates || []).find((x) => x.id === tid);
    return t?.name || null;
  })();

  const activeLinesCount = (calc?.lines || []).filter((ln) => ln.zone_id === zone.id).length;

  const pickCode = (code) => {
    if (!code) return;
    const unit = code.unit || "m2";
    // v6 C5 — for m² codes inside an exterior/general/woodwork zone, default
    // to formula "zone_net" so qty auto-inherits the zone's net area.
    // interior_room zones keep the wall/ceiling flow handled elsewhere.
    let formula;
    if (unit === "m2") {
      formula = (zone.zone_type === "interior_room") ? "wall" : "zone_net";
    } else if (unit === "lm") formula = "linear";
    else if (unit === "piece") formula = "piece";
    else if (unit === "hour") formula = "hour";
    else formula = "fixed";
    onAddMeasurement({
      id: crypto.randomUUID(),
      zone_id: zone.id,
      description: code.description,
      formula,
      length: 0, width: 0, height: 0,
      quantity: 0,
      multiplier: 1, coats: 2,
      price_code_id: code.id,
      action_code: code.code,
    });
    setPicking(false);
  };

  return (
    <div className={`emma-card ${collapsed ? "!p-0 overflow-hidden" : ""}`} data-testid={`zone-card-${zone.id}`}>
      {/* v6 F13 §5D — Ingeklapte kaart-consistentie: exact dezelfde layout
          als RoomQuickFlow (chevron links, icoon, 2 tekstregels, subtotaal
          rechts). Geen afgekapte titel of subtotaal meer op 375 px omdat
          template/count nu op regel 2 staan. Uitgeklapt blijft de bestaande
          layout behouden (met Input voor zone-name). */}
      {collapsed ? (
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="w-full px-4 sm:px-5 py-3 flex items-center gap-3 hover:bg-[#FAF9F6] text-left"
          data-testid={`zone-header-collapsed-${zone.id}`}
        >
          <ChevronRight className="w-4 h-4 text-[#5D7259] flex-shrink-0" />
          <Building2 className="w-4 h-4 text-[#5D7259] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {/* v6 F15.4 — Werkzone-kaarten (Trap, Binnenschrijnwerk,
                Buitenschrijnwerk, Gevel, …) volgen dezelfde visuele
                hiërarchie als Ruimte-kaarten: sjabloonnaam als subtiele
                badge naast de titel, aantal regels op regel 2. Lege staat
                toont "Geen sjabloon gekozen · X regels". */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading font-semibold text-[#2C2C2A] truncate" data-testid={`zone-collapsed-name-${zone.id}`}>
                {zone.name || "Zone"}
              </span>
              {activeTemplateName ? (
                <span
                  className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#F2F5EE] text-[#3F5E3F] max-w-[180px] truncate"
                  data-testid={`zone-collapsed-template-${zone.id}`}
                  title={activeTemplateName}
                >
                  {activeTemplateName}
                </span>
              ) : (
                <span
                  className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#F0EFEA] text-[#A8A7A0]"
                  data-testid={`zone-collapsed-template-${zone.id}`}
                >
                  Geen sjabloon
                </span>
              )}
            </div>
            <div className="text-[11px] text-[#4F4F4D] mt-0.5 flex items-center gap-2 flex-wrap">
              {!activeTemplateName && (
                <>
                  <span data-testid={`zone-collapsed-empty-hint-${zone.id}`}>
                    Geen sjabloon gekozen
                  </span>
                  <span>·</span>
                </>
              )}
              <span data-testid={`zone-collapsed-lines-${zone.id}`}>
                {activeLinesCount} {activeLinesCount === 1 ? "regel" : "regels"}
              </span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-[9px] uppercase tracking-wider text-[#5D7259]">Subtotaal</div>
            <div className="font-heading font-bold text-[#D96C4A]" data-testid={`zone-collapsed-subtotal-${zone.id}`}>
              {fmtEUR(zoneSubtotal)}
            </div>
          </div>
        </button>
      ) : (
        <div className="flex items-start gap-2 mb-3">
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="mt-2.5 flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center hover:bg-[#F0EFEA] text-[#5D7259]"
            data-testid={`zone-collapse-${zone.id}`}
            aria-label="Kaart inklappen"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <Input
            value={zone.name}
            onChange={(e) => onUpdateZone({ name: e.target.value })}
            onFocus={autoSelectOnFocus}
            className="h-11 rounded-xl font-heading font-bold text-lg flex-1 min-w-0"
            data-testid={`zone-name-${zone.id}`}
          />
          <Badge className={`${ZONE_BADGE[zone.zone_type] || ZONE_BADGE.general} h-7 mt-2 flex-shrink-0 hidden sm:inline-flex`}>
            {ZONE_TYPE_LABEL[zone.zone_type] || zone.zone_type.replace("_", " ")}
          </Badge>
          <button
            onClick={onRemoveZone}
            className="w-11 h-11 rounded-xl border border-[#E5E2DC] hover:bg-red-50 flex items-center justify-center text-[#D9534F] flex-shrink-0"
            data-testid={`zone-remove-${zone.id}`}
            aria-label="Verwijder zone"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {!collapsed && <>
      {/* v6 C5 — Zone calc block: rect / triangle / rect_triangle / manual + deductions.
          v6 F42k Fase 3 — Standalone buitenelementen-zone (zone_kind="buitenelementen")
          heeft GEEN gevelvlakken/oppervlakte — elementen worden individueel opgemeten. */}
      {(zone.zone_type === "exterior_area" || zone.zone_type === "woodwork" || zone.zone_type === "general") &&
        zone.zone_kind !== "buitenelementen" && (
        <ZoneCalcBlock
          zone={zone}
          onUpdateZone={onUpdateZone}
          autoMeasureEnabled={autoMeasureEnabled}
          autoMeasureZoneSlot={renderAutoMeasureBlock ? renderAutoMeasureBlock(zone) : null}
        />
      )}
      {zone.zone_type === "interior_room" && (
        <RoomDimsBlock
          zone={zone}
          onUpdateZone={onUpdateZone}
          priceCodes={priceCodes}
          onAddMeasurement={onAddMeasurement}
        />
      )}
      {/* v6 F11.6a → F15 — Joinery (legacy + interior_joinery + exterior_joinery):
          geen geveloppervlakte, alleen objecten. JoineryBlock detecteert het
          exacte zone_type voor kind-filtering en template-context. */}
      {(zone.zone_type === "joinery" ||
        zone.zone_type === "interior_joinery" ||
        zone.zone_type === "exterior_joinery") && (
        <JoineryBlock
          zone={zone}
          onUpdateZone={onUpdateZone}
          priceCodes={priceCodes}
          templates={templates}
          partsDefaults={partsDefaults}
          calc={calc}
        />
      )}
      {/* v6 F12 — Stair-zone: forfait-oppervlakte + trap-context templates */}
      {zone.zone_type === "stair_zone" && (
        <StairZoneBlock zone={zone} onUpdateZone={onUpdateZone} />
      )}

      {/* v6 F12 — Gevel-sjabloon: primaire flow voor buitenzones (na opp.-input) */}
      {zone.zone_type === "exterior_area" && (
        <>
          {/* v6 F42j — Aanvullende buitenelementen (dakoversteek/boeiboord/
              kroonlijst/ander). Staat TUSSEN gevelvlakken en gevelsjabloon.
              Elementen tellen NIET mee in netto zone-oppervlak.
              v6 F42k Fase 3 — Voor zone_kind="buitenelementen" gebruiken we
              de auto-gegroepeerde variant. Voor gebouw-zones (Woning/Chalet/
              Bijgebouw/Bedrijfsgebouw) en legacy zones blijft het klassieke
              FacadeExtraElementBlock behouden. */}
          {zone.zone_kind === "buitenelementen" ? (
            <ExtraElementsGroupedBlock
              zone={zone}
              priceCodes={priceCodes}
              measurements={measurements}
              onUpdateZone={onUpdateZone}
              onAddMeasurement={onAddMeasurement}
              onUpdateMeasurement={onUpdateMeasurement}
              onRemoveMeasurement={onRemoveMeasurement}
            />
          ) : (zone.facade_extra_elements && zone.facade_extra_elements.length > 0) ? (
            /* v6 F42k Correctieronde — Legacy-modus: FacadeExtraElementBlock wordt
               UITSLUITEND getoond wanneer een gewone buitenzone (Woning/Chalet/
               Bijgebouw/Bedrijfsgebouw/Tuinmuur/legacy) reeds oude records heeft.
               Nieuwe buitenelementen worden UITSLUITEND toegevoegd via de
               zelfstandige "Buitenelementen"-zone (ExtraElementsGroupedBlock).
               Zo bestaan er geen twee parallelle invoerflows. */
            <FacadeExtraElementBlock
              zone={zone}
              priceCodes={priceCodes}
              measurements={measurements}
              onUpdateZone={onUpdateZone}
              onAddMeasurement={onAddMeasurement}
              onUpdateMeasurement={onUpdateMeasurement}
              onRemoveMeasurement={onRemoveMeasurement}
              legacyMode
            />
          ) : null}
          {zone.zone_kind !== "buitenelementen" && (
            <ZoneTemplateBlock
              zone={zone}
              measurements={measurements}
              templates={templates}
              priceCodes={priceCodes}
              contexts={["facade_paint", "facade_limewash", "wood_exterior"]}
              label="Gevel-sjabloon"
              accentColor="#6B8E6B"
              mode="facade"
              zoneQty={computeZoneArea(zone).net}
              zoneQtyUnit="m2"
              calc={calc}
              onUpdateZone={onUpdateZone}
              onAddMeasurement={onAddMeasurement}
              onRemoveMeasurement={onRemoveMeasurement}
              onUpdateMeasurement={onUpdateMeasurement}
            />
          )}
        </>
      )}
      {/* v6 F12 — Trap-sjabloon */}
      {zone.zone_type === "stair_zone" && (
        <ZoneTemplateBlock
          zone={zone}
          measurements={measurements}
          templates={templates}
          priceCodes={priceCodes}
          contexts={["stairs"]}
          label="Trap-sjabloon"
          accentColor="#7F4124"
          mode="stair"
          stairTreads={+zone.dim_length || 0}
          zoneQty={+zone.area || 0}
          zoneQtyUnit="m2"
          calc={calc}
          onUpdateZone={onUpdateZone}
          onAddMeasurement={onAddMeasurement}
          onRemoveMeasurement={onRemoveMeasurement}
          onUpdateMeasurement={onUpdateMeasurement}
        />
      )}

      {/* Measurements in this zone — NIET tonen voor joinery (objecten beheren werken zelf).
          v6 F12b — Voor exterior_area en stair_zone worden template-derived
          measurements NIET meer als tweede lijst getoond (die zijn bewerkbaar
          via het sjabloon-block hierboven). Enkel manueel toegevoegde
          "Code toevoegen"-regels verschijnen hier als "Extra werken (buiten sjabloon)".
          v6 F42j-HOTFIX — Measurements gekoppeld aan een FacadeExtraElement
          (via extra_element_id) horen NOOIT in deze extra-werken-bucket: die
          worden reeds gerenderd door FacadeExtraElementBlock hierboven. Zonder
          deze filter zou een dakoversteek/boeiboord/kroonlijst-werk dubbel
          verschijnen én als "buiten sjabloon" ogen. */}
      {/* v6 F11.6a → F15 — Measurements & foto's overslaan voor alle joinery-varianten */}
      {zone.zone_type !== "joinery" &&
       zone.zone_type !== "interior_joinery" &&
       zone.zone_type !== "exterior_joinery" && (() => {
        const isTemplateZone = zone.zone_type === "exterior_area" || zone.zone_type === "stair_zone";
        // Determine which measurements are already rendered by the template block:
        // any measurement whose price_code_id appears in the active template's lines.
        let templatePcSet = new Set();
        if (isTemplateZone && zone.zone_template_id) {
          const tpl = (templates || []).find((t) => t.id === zone.zone_template_id);
          templatePcSet = new Set((tpl?.template_lines || []).map((l) => l.price_code_id));
        }
        // v6 F42j-HOTFIX — extra_element_id measurements uit deze lijst filteren.
        const extraMeasurements = isTemplateZone
          ? measurements.filter((m) => !m.extra_element_id && (!m.price_code_id || !templatePcSet.has(m.price_code_id)))
          : measurements.filter((m) => !m.extra_element_id);
        const showEmpty = measurements.length === 0;
        const showExtraHeader = isTemplateZone && extraMeasurements.length > 0;
        return (
          <div className="space-y-2">
            {showEmpty && (
              <div className="text-sm text-[#4F4F4D] italic py-3 border-b border-dashed border-[#E5E2DC]">
                {isTemplateZone
                  ? 'Kies een sjabloon hierboven of klik "Code toevoegen" voor een handmatige regel.'
                  : 'Geen meetlijnen. Klik "Code toevoegen" om te starten.'}
              </div>
            )}
            {showExtraHeader && (
              <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#5D7259] pt-2">
                Extra werken (buiten sjabloon)
              </div>
            )}
            {extraMeasurements.map((m) => {
              const pc = priceCodes.find((p) => p.id === m.price_code_id);
              const ln = (calc?.lines || []).find((x) => x.item_id === m.id);
              return (
                <ZoneMeasurementRow
                  key={m.id}
                  m={m}
                  pc={pc}
                  ln={ln}
                  onUpdate={(patch) => onUpdateMeasurement(m.id, patch)}
                  onRemove={() => onRemoveMeasurement(m.id)}
                />
              );
            })}
          </div>
        );
      })()}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E5E2DC]">
        {(zone.zone_type === "joinery" ||
          zone.zone_type === "interior_joinery" ||
          zone.zone_type === "exterior_joinery") ? (
          <div className="text-xs text-[#4F4F4D] italic">
            {zone.zone_type === "interior_joinery"
              ? "Binnenschrijnwerk — voeg objecten toe via de knoppen hierboven."
              : zone.zone_type === "exterior_joinery"
                ? "Buitenschrijnwerk — voeg objecten toe via de knoppen hierboven."
                : "Schrijnwerk-zone — voeg objecten toe via de knoppen hierboven."}
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={() => setPicking(true)}
            className="h-10 rounded-xl gap-1"
            data-testid={`zone-add-line-${zone.id}`}
          >
            <Plus className="w-4 h-4" /> Code toevoegen
          </Button>
        )}
        <div className="text-sm">
          <span className="text-[#4F4F4D]">Subtotaal:</span>{" "}
          <strong className="text-[#2C2C2A]" data-testid={`zone-subtotal-${zone.id}`}>{fmtEUR(zoneSubtotal)}</strong>
        </div>
      </div>

      {/* v6 F11.6a → F15 — Foto's overslaan voor alle joinery-varianten */}
      {zone.zone_type !== "joinery" &&
       zone.zone_type !== "interior_joinery" &&
       zone.zone_type !== "exterior_joinery" && (
        <ZonePhotoStrip
          zone={zone}
          quoteSaved={quoteSaved}
          onUpload={onPhotoUpload}
          onRemove={onPhotoRemove}
          onSetLabel={onPhotoSetLabel}
        />
      )}
      {picking && (
        <PriceCodeQuickPicker
          priceCodes={priceCodes}
          onPick={(pc) => pickCode(pc)}
          onClose={() => setPicking(false)}
        />
      )}
      </>}
    </div>
  );
}

// ---------- Simple grouped price-code picker (modal) ----------
function PriceCodeQuickPicker({ priceCodes, onPick, onClose }) {
  const [q, setQ] = useState("");
  const filtered = priceCodes.filter((p) => {
    if (!p.active) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return p.code.toLowerCase().includes(t) || (p.description || "").toLowerCase().includes(t);
  });
  // Group by work_category
  const groups = (() => {
    const m = new Map();
    for (const it of filtered) {
      const k = it.work_category || "Overige";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(it);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  })();
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="zone-pc-picker">
        <DialogHeader>
          <DialogTitle>Prijscode kiezen</DialogTitle>
          <DialogDescription className="text-xs text-[#4F4F4D]">
            Kies een code uit de catalogus om deze als meetlijn aan de werkzone toe te voegen.
          </DialogDescription>
        </DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek code of omschrijving..."
          className="h-12 rounded-xl" autoFocus data-testid="zone-pc-picker-search" />
        <div className="space-y-3 mt-2">
          {groups.map(([cat, rows]) => (
            <div key={cat}>
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#5D7259] mb-1 px-1">{cat}</div>
              <div className="space-y-1">
                {rows.map((pc) => (
                  <button key={pc.id} onClick={() => onPick(pc)}
                    className="w-full text-left p-2.5 rounded-lg hover:bg-[#FFF8F2] flex items-center gap-3"
                    data-testid={`zone-pc-pick-${pc.code}`}>
                    <div className="font-mono text-xs text-[#4F4F4D] w-16">{pc.code}</div>
                    <div className="flex-1 text-sm text-[#2C2C2A]">{pc.description}</div>
                    <div className="text-xs text-[#5D7259]">{UNIT_LABEL[pc.unit]}</div>
                    <div className="text-sm font-bold font-mono">€ {(+pc.default_price).toFixed(2)}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- v6 C5 — Zone calc block (rect / triangle / rect+triangle / manual + deductions) ----------
const CALC_MODES = [
  { value: "rect",          label: "Rechthoek",         hint: "L × H" },
  { value: "triangle",      label: "Puntgevel",         hint: "(L × H) / 2" },
  { value: "rect_triangle", label: "Rechthoek + Punt",  hint: "L×H1 + (L×H2)/2" },
  { value: "manual",        label: "Manueel",           hint: "m² invullen" },
];

function ZoneCalcBlock({ zone, onUpdateZone, autoMeasureEnabled = false, autoMeasureZoneSlot = null }) {
  // v6 F42 — Gevelwerken v2: multi-surface dispatch (alleen voor exterior_area).
  // Legacy zones met facade_surfaces=[] blijven de bestaande single-surface UI
  // tonen. Bij eerste klik op "+ Gevelvlak toevoegen" wordt de bestaande legacy
  // meting overgezet naar Gevelvlak 1 (lazy migration) en wordt Gevelvlak 2
  // toegevoegd. Legacy velden (calc_mode/dim_length/…) worden BEWUST niet
  // gewist voor rollback-veiligheid; ze worden genegeerd zolang
  // facade_surfaces gevuld is (zowel front-end als backend).
  const isFacadeZone = zone.zone_type === "exterior_area";
  const hasSurfaces = isFacadeZone && (zone.facade_surfaces || []).length > 0;

  if (isFacadeZone && hasSurfaces) {
    return (
      <FacadeMultiSurfaceBlock
        zone={zone}
        onUpdateZone={onUpdateZone}
        autoMeasureEnabled={autoMeasureEnabled}
        autoMeasureZoneSlot={autoMeasureZoneSlot}
      />
    );
  }

  const mode = zone.calc_mode || "manual";
  const { gross, deduction, net } = computeZoneArea(zone);

  const setMode = (m) => onUpdateZone({ calc_mode: m });
  const addDeduction = () => {
    const next = [
      ...(zone.deductions || []),
      { id: crypto.randomUUID(), kind: "window", name: "", length: 0, height: 0 },
    ];
    onUpdateZone({ deductions: next });
  };
  const updDeduction = (idx, patch) => {
    const next = (zone.deductions || []).map((d, i) => (i === idx ? { ...d, ...patch } : d));
    onUpdateZone({ deductions: next });
  };
  const remDeduction = (idx) => {
    const next = (zone.deductions || []).filter((_, i) => i !== idx);
    onUpdateZone({ deductions: next });
  };

  // v6 F42 — Lazy migration: converteer bestaande legacy dims naar de eerste
  // FacadeSurface en voeg een tweede leeg gevelvlak toe (spec Fase 1: knop
  // "+ Gevelvlak toevoegen" moet consistent één extra vlak leveren bovenop
  // de migratie). Aftrekken gaan volledig mee met Gevelvlak 1 (rollup blijft
  // identiek: zone-netto is gelijk vóór en na klik zolang vlak 2 nog leeg is).
  // "rect_triangle" splitst in twee surfaces (rect + triangle) omdat het
  // nieuwe model dat niet als één shape ondersteunt — daarna wordt géén
  // extra leeg vlak toegevoegd (user heeft al twee vlakken).
  const addFirstFacadeSurface = () => {
    const legacyDeductions = (zone.deductions || []).map((d) => ({
      id: d.id || crypto.randomUUID(),
      name: d.name || "",
      kind: d.kind || null,               // v6 F42b — behoud aftrek-type
      length: +d.length || 0,
      height: +d.height || 0,
    }));
    const legacyMode = zone.calc_mode || "manual";
    const L = +zone.dim_length || 0;
    const H1 = +zone.dim_height || 0;
    const H2 = +zone.dim_height2 || 0;

    const surfaces = [];
    if (legacyMode === "rect_triangle") {
      surfaces.push({
        id: crypto.randomUUID(),
        name: "Gevelvlak 1",
        order: 0,
        shape_type: "rect",
        dim_length: L,
        dim_height: H1,
        dim_height2: 0,
        manual_area: 0,
        deductions: legacyDeductions,        // alle aftrekken naar het rechthoekvlak
        surface_type: null,
      });
      surfaces.push({
        id: crypto.randomUUID(),
        name: "Gevelvlak 2 — Puntgevel",
        order: 1,
        shape_type: "triangle",
        dim_length: L,
        dim_height: H2,
        dim_height2: 0,
        manual_area: 0,
        deductions: [],
        surface_type: null,
      });
    } else {
      const shape = legacyMode === "triangle" ? "triangle"
        : legacyMode === "manual" ? "manual"
        : "rect"; // "rect" of onbekend → rect
      surfaces.push({
        id: crypto.randomUUID(),
        name: "Gevelvlak 1",
        order: 0,
        shape_type: shape,
        dim_length: L,
        dim_height: H1,
        dim_height2: 0,
        manual_area: shape === "manual" ? (+zone.area || 0) : 0,
        deductions: legacyDeductions,
        surface_type: null,
      });
      // Extra leeg vlak 2 (spec: "waarna vlak 2 wordt toegevoegd")
      surfaces.push({
        id: crypto.randomUUID(),
        name: "Gevelvlak 2",
        order: 1,
        shape_type: "rect",
        dim_length: 0,
        dim_height: 0,
        dim_height2: 0,
        manual_area: 0,
        deductions: [],
        surface_type: null,
      });
    }
    onUpdateZone({ facade_surfaces: surfaces });
  };


  return (
    <div className="mb-3 p-3 rounded-xl bg-[#F2F5EE] border border-[#6B8E6B]/30 space-y-3" data-testid={`zone-calc-${zone.id}`}>
      <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#6B8E6B] flex items-center gap-2">
        Geveloppervlakte
      </div>

      {/* Mode selector */}
      <div className="flex flex-wrap gap-1 bg-white rounded-lg p-1" data-testid={`zone-calc-mode-${zone.id}`}>
        {CALC_MODES.map((c) => (
          <button
            key={c.value}
            onClick={() => setMode(c.value)}
            className={`flex-1 min-w-[80px] px-2 h-9 rounded-md text-xs font-medium transition ${mode === c.value ? "bg-[#6B8E6B] text-white shadow" : "text-[#4F4F4D] hover:bg-[#F0EFEA]"}`}
            data-testid={`zone-calc-mode-${c.value}-${zone.id}`}
            title={c.hint}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Dimension inputs per mode */}
      {mode === "rect" && (
        <div className="flex flex-wrap items-end gap-3">
          <DimInput value={zone.dim_length} onChange={(v) => onUpdateZone({ dim_length: v })} label="Lengte (m)" testid={`zone-L-${zone.id}`} width="w-24" />
          <span className="text-[#A8A7A0] pb-3">×</span>
          <DimInput value={zone.dim_height} onChange={(v) => onUpdateZone({ dim_height: v })} label="Hoogte (m)" testid={`zone-H-${zone.id}`} width="w-24" />
          <div className="text-xs text-[#4F4F4D] pb-3 ml-2">= <strong className="text-[#2C2C2A]">{(gross).toFixed(2)} m²</strong> bruto</div>
        </div>
      )}
      {mode === "triangle" && (
        <div className="flex flex-wrap items-end gap-3">
          <DimInput value={zone.dim_length} onChange={(v) => onUpdateZone({ dim_length: v })} label="Lengte (m)" testid={`zone-L-${zone.id}`} width="w-24" />
          <span className="text-[#A8A7A0] pb-3">×</span>
          <DimInput value={zone.dim_height} onChange={(v) => onUpdateZone({ dim_height: v })} label="Hoogte (m)" testid={`zone-H-${zone.id}`} width="w-24" />
          <span className="text-[#A8A7A0] pb-3">/ 2</span>
          <div className="text-xs text-[#4F4F4D] pb-3 ml-2">= <strong className="text-[#2C2C2A]">{(gross).toFixed(2)} m²</strong> bruto</div>
        </div>
      )}
      {mode === "rect_triangle" && (
        <div className="flex flex-wrap items-end gap-3">
          <DimInput value={zone.dim_length} onChange={(v) => onUpdateZone({ dim_length: v })} label="Breedte L (m)" testid={`zone-L-${zone.id}`} width="w-24" />
          <DimInput value={zone.dim_height} onChange={(v) => onUpdateZone({ dim_height: v })} label="Rechth. H1" testid={`zone-H1-${zone.id}`} width="w-24" />
          <DimInput value={zone.dim_height2} onChange={(v) => onUpdateZone({ dim_height2: v })} label="Punt H2" testid={`zone-H2-${zone.id}`} width="w-24" />
          <div className="text-xs text-[#4F4F4D] pb-3 ml-2">= <strong className="text-[#2C2C2A]">{(gross).toFixed(2)} m²</strong> bruto</div>
        </div>
      )}
      {mode === "manual" && (
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col items-start">
            <RawNumInput
              value={zone.area ?? 0}
              onChange={(v) => onUpdateZone({ area: typeof v === "number" ? v : 0 })}
              placeholder="m²"
              className="h-9 w-28 rounded-lg text-right font-mono"
              data-testid={`zone-area-${zone.id}`}
            />
            <span className="text-[10px] text-[#5D7259] mt-0.5">Oppervlakte (m²)</span>
          </div>
          <div className="text-xs text-[#4F4F4D] pb-3 ml-2">Bruto = <strong className="text-[#2C2C2A]">{(gross).toFixed(2)} m²</strong></div>
        </div>
      )}

      {/* Deductions */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#5D7259]">
            Aftrekken (deuren · ramen · poort)
          </div>
          <button
            onClick={addDeduction}
            className="text-xs px-2 h-7 rounded-md border border-[#E5E2DC] hover:border-[#6B8E6B] hover:bg-white text-[#2C2C2A] font-medium gap-1 inline-flex items-center"
            data-testid={`zone-add-deduction-${zone.id}`}
          >
            <Plus className="w-3 h-3" /> Aftrek
          </button>
        </div>
        {(zone.deductions || []).length === 0 ? (
          <div className="text-[11px] text-[#5D7259] italic px-1">Geen openingen of aftrekposten toegevoegd</div>
        ) : (
          <div className="space-y-1.5">
            {(zone.deductions || []).map((d, idx) => (
              <DeductionRow
                key={d.id || idx}
                deduction={d}
                testidPrefix={`zone-deduction-${zone.id}-${idx}`}
                onUpdate={(patch) => updDeduction(idx, patch)}
                onRemove={() => remDeduction(idx)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Gross / deductions / net summary */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-2 border-t border-[#6B8E6B]/20 text-xs" data-testid={`zone-area-summary-${zone.id}`}>
        <span>Bruto: <strong className="font-mono">{gross.toFixed(2)} m²</strong></span>
        <span className="text-[#D9534F]">− Aftrekposten: <strong className="font-mono">{deduction.toFixed(2)} m²</strong></span>
        <span className="ml-auto text-sm">
          Netto: <strong className="font-mono text-[#6B8E6B]" data-testid={`zone-area-net-${zone.id}`}>{net.toFixed(2)} m²</strong>
        </span>
      </div>

      {/* v6 F42 — Lazy migration entry-point voor gevel-zones. Enkel tonen op
          exterior_area (buitengevels/tuinmuur/bijgebouw). Bij eerste klik wordt
          de bestaande legacy meting overgezet naar Gevelvlak 1. */}
      {zone.zone_type === "exterior_area" && (
        <div className="pt-2 border-t border-dashed border-[#6B8E6B]/25 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={addFirstFacadeSurface}
            className="text-xs px-3 h-9 rounded-lg border-2 border-dashed border-[#6B8E6B]/50 hover:border-[#6B8E6B] hover:bg-white text-[#3E5A3E] font-medium gap-1 inline-flex items-center"
            data-testid={`zone-add-facade-surface-${zone.id}`}
          >
            <Plus className="w-3.5 h-3.5" /> Gevelvlak toevoegen
          </button>
          <span className="text-[10px] text-[#5D7259] italic">
            Meerdere gevelvlakken (bv. gelijkvloers + puntgevel + aanbouw)
          </span>
        </div>
      )}
    </div>
  );
}

// v6 F42 — Gevelwerken v2: multi-surface UI. Rendert boven de zone een
// zone-totaal-samenvatting, daaronder een lijst van FacadeSurfaceRow's,
// met een knop om extra gevelvlakken toe te voegen. Eerste vlak default
// open, overige ingeklapt (Fase 1 goedgekeurd).
function FacadeMultiSurfaceBlock({
  zone, onUpdateZone,
  autoMeasureEnabled = false,
  autoMeasureZoneSlot = null,   // v14ao — rendered above surfaces list
}) {
  const surfaces = zone.facade_surfaces || [];
  const { gross, deduction, net } = computeZoneArea(zone);

  const updateSurface = (idx, patch) => {
    const next = surfaces.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onUpdateZone({ facade_surfaces: next });
  };
  const removeSurface = (idx) => {
    const next = surfaces.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i }));
    onUpdateZone({ facade_surfaces: next });
  };
  const addSurface = () => {
    const next = [
      ...surfaces,
      {
        id: crypto.randomUUID(),
        name: `Gevelvlak ${surfaces.length + 1}`,
        order: surfaces.length,
        shape_type: "rect",
        dim_length: 0,
        dim_height: 0,
        dim_height2: 0,
        manual_area: 0,
        deductions: [],
        surface_type: null,
      },
    ];
    onUpdateZone({ facade_surfaces: next });
  };

  return (
    <div
      className="mb-3 p-3 rounded-xl bg-[#F2F5EE] border border-[#6B8E6B]/30 space-y-3"
      data-testid={`zone-facade-multi-${zone.id}`}
    >
      {/* v14ao — AutoMeasure™ zone-block: tussen zone-header en GEVELVLAKKEN. */}
      {autoMeasureZoneSlot}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#6B8E6B]">
          Gevelvlakken ({surfaces.length})
        </div>
        <div className="text-[10px] text-[#5D7259] italic">
          Zone-totaal = som van netto per vlak
        </div>
      </div>

      <div className="space-y-2" data-testid={`zone-facade-surfaces-list-${zone.id}`}>
        {surfaces.map((s, idx) => (
          <FacadeSurfaceRow
            key={s.id || idx}
            surface={s}
            index={idx}
            zoneId={zone.id}
            defaultCollapsed={idx > 0}
            onUpdate={(patch) => updateSurface(idx, patch)}
            onRemove={() => removeSurface(idx)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={addSurface}
        className="w-full text-sm px-3 h-10 rounded-lg border-2 border-dashed border-[#6B8E6B]/50 hover:border-[#6B8E6B] hover:bg-white text-[#3E5A3E] font-medium gap-1 inline-flex items-center justify-center"
        data-testid={`zone-facade-add-surface-${zone.id}`}
      >
        <Plus className="w-4 h-4" /> Gevelvlak toevoegen
      </button>

      {/* Zone-totaal */}
      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-2 border-t border-[#6B8E6B]/25 text-xs"
        data-testid={`zone-facade-summary-${zone.id}`}
      >
        <span>Zone bruto: <strong className="font-mono">{gross.toFixed(2)} m²</strong></span>
        <span className="text-[#D9534F]">− Aftrek: <strong className="font-mono">{deduction.toFixed(2)} m²</strong></span>
        <span className="ml-auto text-sm">
          Zone netto: <strong className="font-mono text-[#6B8E6B]" data-testid={`zone-facade-net-${zone.id}`}>{net.toFixed(2)} m²</strong>
        </span>
      </div>
    </div>
  );
}

// ---------- Smart measurement row with auto-dimension inputs ----------
// v6 C4 — supports formula modes:
//   wall      : qty = L × H
//   ceiling   : qty = L × W
//   triangle  : qty = (L × H) / 2  (topgevel)
//   linear    : qty (lm) stored in length
//   piece     : qty (stuks)
//   hour      : qty (uren)
//   fixed     : qty=1 forfait
//   custom    : manual qty (m²)
// For m²-unit codes, default formula is the row's `formula` field (often
// "custom" from template apply); user can switch to wall/ceiling/triangle
// via a small popover.
const FORMULA_LABEL = {
  zone_net:"Auto netto",
  wall:    "Wand L×H",
  ceiling: "Plafond L×B",
  triangle:"Driehoek (L×H)/2",
  custom:  "Manueel",
  linear:  "Lopende meter",
  piece:   "Stuks",
  hour:    "Uren",
  fixed:   "Forfait",
};

function ZoneMeasurementRow({ m, pc, ln, onUpdate, onRemove }) {
  const isM2Code = pc?.unit === "m2";
  const f = m.formula || (isM2Code ? "zone_net" : (pc?.unit === "lm" ? "linear" : pc?.unit === "piece" ? "piece" : pc?.unit === "hour" ? "hour" : "fixed"));
  const showLH = f === "wall" || f === "triangle";
  const showLW = f === "ceiling";
  const showLinear = f === "linear";
  const showAuto = f === "zone_net";
  const showManualQty = !showLH && !showLW && !showLinear && !showAuto;

  // For m² codes the user can switch between auto/manual/wall/ceiling/triangle.
  const formulaOptions = isM2Code ? ["zone_net", "custom", "wall", "ceiling", "triangle"] : null;

  return (
    <div className="p-2.5 rounded-lg border border-[#E5E2DC] space-y-2" data-testid={`zone-line-${m.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[160px]">
          <div className="text-sm font-medium text-[#2C2C2A]">
            <EditableDescription
              value={m.description_snapshot || pc?.description || m.description || "—"}
              override={m.description_override}
              defaultValue={m.description_snapshot || pc?.description || m.description || "—"}
              onOverride={(text) => onUpdate({ description_override: text })}
              onReset={() => onUpdate({ description_override: null })}
              testId={`zone-line-desc-${m.id}`}
            />
          </div>
          <div className="text-[10px] text-[#5D7259] font-mono">{pc?.code || m.action_code || "—"} · {UNIT_LABEL[pc?.unit] || "-"}</div>
        </div>

        {/* Formula switcher — only for m² codes */}
        {formulaOptions && (
          <select
            value={f}
            onChange={(e) => onUpdate({ formula: e.target.value })}
            className="h-9 rounded-lg border border-[#E5E2DC] bg-white text-xs px-2 font-medium"
            data-testid={`zone-line-formula-${m.id}`}
          >
            {formulaOptions.map((opt) => (
              <option key={opt} value={opt}>{FORMULA_LABEL[opt]}</option>
            ))}
          </select>
        )}

        {/* Dimension or quantity inputs */}
        {showLH && (
          <>
            <DimInput value={m.length} onChange={(v) => onUpdate({ length: v })} label="L" testid={`zone-line-L-${m.id}`} />
            <DimInput value={m.height} onChange={(v) => onUpdate({ height: v })} label="H" testid={`zone-line-H-${m.id}`} />
          </>
        )}
        {showLW && (
          <>
            <DimInput value={m.length} onChange={(v) => onUpdate({ length: v })} label="L" testid={`zone-line-L-${m.id}`} />
            <DimInput value={m.width} onChange={(v) => onUpdate({ width: v })} label="B" testid={`zone-line-W-${m.id}`} />
          </>
        )}
        {showLinear && (
          <DimInput value={m.length} onChange={(v) => onUpdate({ length: v })} label="lm" testid={`zone-line-lm-${m.id}`} width="w-24" />
        )}
        {showAuto && (
          <div className="px-2.5 h-9 rounded-lg bg-[#F2F5EE] border border-[#6B8E6B]/30 flex items-center gap-1.5 text-xs text-[#6B8E6B] font-medium" data-testid={`zone-line-auto-${m.id}`}>
            <span className="text-[10px] uppercase tracking-wider">Netto zone</span>
            <strong className="font-mono">{(ln?.quantity || 0).toFixed(2)} m²</strong>
          </div>
        )}
        {showManualQty && (
          <DimInput value={m.quantity} onChange={(v) => onUpdate({ quantity: v })} label={UNIT_LABEL[pc?.unit] || "qty"} testid={`zone-line-qty-${m.id}`} width="w-24" />
        )}

        <RawNumInput
          value={m.unit_price_override ?? ""}
          allowEmpty
          onChange={(v) => onUpdate({ unit_price_override: v === "" ? null : v })}
          placeholder={pc?.default_price?.toFixed(2)}
          className="h-9 w-24 rounded-lg text-right font-mono"
          data-testid={`zone-line-price-${m.id}`}
        />
        <div className="w-24 text-right text-sm font-bold" data-testid={`zone-line-total-${m.id}`}>
          {fmtEUR(ln?.total_excl || 0)}
        </div>
        <button onClick={onRemove} className="w-9 h-9 rounded-lg hover:bg-red-50 flex items-center justify-center text-[#D9534F]"
          data-testid={`zone-line-remove-${m.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {(showLH || showLW) && (
        <div className="text-[10px] text-[#5D7259] pl-1">
          = {showLH ? `${(+m.length || 0).toFixed(2)} × ${(+m.height || 0).toFixed(2)}` : `${(+m.length || 0).toFixed(2)} × ${(+m.width || 0).toFixed(2)}`}
          {f === "triangle" && " / 2"}
          {" = "}
          <strong className="text-[#2C2C2A]">{(ln?.gross || 0).toFixed(2)} m²</strong>
        </div>
      )}
    </div>
  );
}

function DimInput({ value, onChange, label, testid, width = "w-16" }) {
  return (
    <div className="flex flex-col items-center">
      <RawNumInput
        value={value ?? 0}
        onChange={(v) => onChange(typeof v === "number" ? v : 0)}
        className={`h-9 ${width} rounded-lg text-right font-mono`}
        data-testid={testid}
      />
      <span className="text-[9px] text-[#5D7259] mt-0.5">{label}</span>
    </div>
  );
}

// ---------- Interior_room dimension block (L × B × H + "Volledige kamer" auto-add) ----------
// Renders 3 large inputs at the top of an interior_room zone. The "Walls + Ceiling
// toevoegen" button creates two measurement lines from the dimensions:
//   - Walls   : formula="wall",    L=L, H=H, W=W  (qty resolved server-side via 2(L+W)*H)
//   - Ceiling : formula="ceiling", L=L, W=W       (qty=L*W)
// Uses S-03.01 (Muren standaard) and S-03.03 (Plafonds standaard) by default.
function RoomDimsBlock({ zone, onUpdateZone, priceCodes, onAddMeasurement }) {
  const L = +zone.length || 0;
  const W = +zone.width || 0;
  const H = +zone.height || 0;
  const wallArea = 2 * (L + W) * H;
  const ceilArea = L * W;

  // v6 C4 — current Measurement schema has formula='wall' = L×H only (single wall).
  // For a "Volledige kamer" we generate ONE wall measurement with quantity=2(L+W)*H
  // stored as `quantity` and formula='custom', preserving dimensions in length/width/height
  // for traceability + audit. This stays consistent with the backend qty engine.
  const applyFullRoom = () => {
    if (!L || !W || !H) return;
    const walls = priceCodes.find((p) => p.code === "S-03.01");
    const ceiling = priceCodes.find((p) => p.code === "S-03.03");
    if (walls) {
      onAddMeasurement({
        id: crypto.randomUUID(),
        zone_id: zone.id,
        description: "Muren schilderen (volledige kamer)",
        formula: "custom",
        length: L, width: W, height: H,
        quantity: +(wallArea).toFixed(2),
        multiplier: 1, coats: 2,
        price_code_id: walls.id,
        action_code: walls.code,
      });
    }
    if (ceiling) {
      onAddMeasurement({
        id: crypto.randomUUID(),
        zone_id: zone.id,
        description: "Plafond schilderen",
        formula: "ceiling",
        length: L, width: W, height: 0,
        quantity: 0,
        multiplier: 1, coats: 2,
        price_code_id: ceiling.id,
        action_code: ceiling.code,
      });
    }
  };

  return (
    <div className="mb-3 p-3 rounded-xl bg-[#FFF8F2] border border-[#D96C4A]/20" data-testid={`room-dims-${zone.id}`}>
      <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#D96C4A] mb-2">Kamer-afmetingen</div>
      <div className="flex flex-wrap items-end gap-3">
        <DimInput value={zone.length} onChange={(v) => onUpdateZone({ length: v })} label="Lengte" testid={`room-L-${zone.id}`} width="w-20" />
        <span className="text-[#A8A7A0] pb-3">×</span>
        <DimInput value={zone.width} onChange={(v) => onUpdateZone({ width: v })} label="Breedte" testid={`room-W-${zone.id}`} width="w-20" />
        <span className="text-[#A8A7A0] pb-3">×</span>
        <DimInput value={zone.height} onChange={(v) => onUpdateZone({ height: v })} label="Hoogte" testid={`room-H-${zone.id}`} width="w-20" />
        <div className="text-xs text-[#4F4F4D] ml-2 pb-3">
          <div>Wanden: <strong>{wallArea.toFixed(2)} m²</strong></div>
          <div>Plafond: <strong>{ceilArea.toFixed(2)} m²</strong></div>
        </div>
        <Button
          onClick={applyFullRoom}
          disabled={!L || !W || !H}
          className="h-10 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] gap-1 text-xs ml-auto"
          data-testid={`room-apply-full-${zone.id}`}
        >
          <Plus className="w-3 h-3" /> Muren + Plafond
        </Button>
      </div>
    </div>
  );
}

// v6 F12b — Stair-zone dimensies met correcte formule:
//   Open trap:     treden × breedte × aantrede
//   Gesloten trap: treden × breedte × (aantrede + optrede)
// Toont formule leesbaar. Oppervlakte wordt AUTO-berekend en gaat naar
// zone.area zodat m²-regels in het Trap-sjabloon (na opt-in per regel)
// hem kunnen gebruiken.
function StairZoneBlock({ zone, onUpdateZone }) {
  const treads = +zone.dim_length || 0;
  const width = +zone.dim_height || 0;
  const treadDepth = zone.stair_tread_depth ?? 0.28;
  const riserHeight = zone.stair_riser_height ?? 0.18;
  const stairType = zone.stair_type || "closed";

  const multiplier = stairType === "closed" ? (treadDepth + riserHeight) : treadDepth;
  const computed = treads > 0 && width > 0 && multiplier > 0
    ? +(treads * width * multiplier).toFixed(2)
    : 0;

  // Auto-sync computed → zone.area zodat template m²-regels (opt-in) hem kunnen
  // gebruiken. Doen we via effect (idempotent — alleen bij mismatch).
  useEffect(() => {
    if (computed !== (+zone.area || 0)) {
      onUpdateZone({ area: computed });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed]);

  const formulaText = stairType === "closed"
    ? `${treads} treden × ${width.toFixed(2)} m × (${treadDepth.toFixed(2)} + ${riserHeight.toFixed(2)}) m`
    : `${treads} treden × ${width.toFixed(2)} m × ${treadDepth.toFixed(2)} m`;

  return (
    <div
      className="mb-3 p-3 rounded-xl bg-[#F7EDE4]/60 border border-[#7F4124]/20 space-y-2.5"
      data-testid={`stair-block-${zone.id}`}
    >
      <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#7F4124] flex items-center gap-2">
        Trap-afmetingen
      </div>

      {/* Type toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wider text-[#5D7259] font-bold">Type:</span>
        <div className="flex gap-1 bg-white rounded-lg p-0.5" data-testid={`stair-type-${zone.id}`}>
          {[
            { value: "open",   label: "Open"     },
            { value: "closed", label: "Gesloten" },
          ].map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => onUpdateZone({ stair_type: o.value })}
              className={`px-3 h-8 rounded-md text-xs font-medium ${stairType === o.value ? "bg-[#7F4124] text-white shadow" : "text-[#4F4F4D] hover:text-[#2C2C2A]"}`}
              data-testid={`stair-type-${zone.id}-${o.value}`}
            >
              {o.label} trap
            </button>
          ))}
        </div>
      </div>

      {/* Inputs — mobile: 2 per row via flex-wrap */}
      <div className="flex flex-wrap items-end gap-2.5">
        <div className="flex flex-col items-start">
          <RawNumInput
            value={treads}
            onChange={(v) => onUpdateZone({ dim_length: typeof v === "number" ? v : 0 })}
            className="h-9 w-16 rounded-lg text-right font-mono"
            data-testid={`stair-treads-${zone.id}`}
          />
          <span className="text-[10px] text-[#5D7259] mt-0.5">Treden</span>
        </div>
        <div className="flex flex-col items-start">
          <RawNumInput
            value={width}
            onChange={(v) => onUpdateZone({ dim_height: typeof v === "number" ? v : 0 })}
            className="h-9 w-20 rounded-lg text-right font-mono"
            data-testid={`stair-width-${zone.id}`}
          />
          <span className="text-[10px] text-[#5D7259] mt-0.5">Breedte (m)</span>
        </div>
        <div className="flex flex-col items-start">
          <RawNumInput
            value={treadDepth}
            onChange={(v) => onUpdateZone({ stair_tread_depth: typeof v === "number" ? v : 0.28 })}
            className="h-9 w-20 rounded-lg text-right font-mono"
            data-testid={`stair-tread-depth-${zone.id}`}
          />
          <span className="text-[10px] text-[#5D7259] mt-0.5">Aantrede (m)</span>
        </div>
        {stairType === "closed" && (
          <div className="flex flex-col items-start">
            <RawNumInput
              value={riserHeight}
              onChange={(v) => onUpdateZone({ stair_riser_height: typeof v === "number" ? v : 0.18 })}
              className="h-9 w-20 rounded-lg text-right font-mono"
              data-testid={`stair-riser-height-${zone.id}`}
            />
            <span className="text-[10px] text-[#5D7259] mt-0.5">Optrede (m)</span>
          </div>
        )}
      </div>

      {/* Compacte formule (rustiger dan de vorige versie) */}
      <div className="text-[11px] text-[#5D7259] font-mono flex flex-wrap items-baseline gap-1" data-testid={`stair-formula-${zone.id}`}>
        <span>{formulaText}</span>
        <span>=</span>
        <strong className="text-[#7F4124] text-sm">{computed.toFixed(2)} m²</strong>
      </div>
    </div>
  );
}

// ---------- Photo strip ----------
// v6 C3 — supports Voor/Na labels. Normalizes legacy str-photos to dicts.
function ZonePhotoStrip({ zone, quoteSaved, onUpload, onRemove, onSetLabel }) {
  const [drag, setDrag] = useState(false);
  const [uploadLabel, setUploadLabel] = useState("before"); // "before"|"after"
  const photos = (zone.photos || []).map((p) =>
    typeof p === "string" ? { data_url: p, label: "before" } : p
  );
  const maxed = photos.length >= 6;

  const handleFiles = async (files) => {
    if (!quoteSaved) return;
    for (const f of Array.from(files || []).slice(0, 6 - photos.length)) {
      if (!f.type.startsWith("image/")) continue;
      const url = await compressImage(f, 1200, 0.72);
      await onUpload(url, uploadLabel);
    }
  };

  const beforeCount = photos.filter((p) => p.label === "before").length;
  const afterCount = photos.filter((p) => p.label === "after").length;

  return (
    <div className="mt-3 pt-3 border-t border-[#E5E2DC]">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#5D7259] flex items-center gap-2">
          <Camera className="w-3.5 h-3.5" /> Foto's ({photos.length}/6)
          {!quoteSaved && <span className="ml-2 normal-case text-[#D9534F] font-normal text-[10px]">(Eerst offerte opslaan)</span>}
        </div>
        {(beforeCount > 0 || afterCount > 0) && (
          <div className="text-[10px] text-[#4F4F4D] flex gap-2">
            {beforeCount > 0 && <span className="px-2 py-0.5 rounded bg-[#FFF8F2] text-[#D96C4A] font-medium">Voor: {beforeCount}</span>}
            {afterCount > 0 && <span className="px-2 py-0.5 rounded bg-[#E8F0E5] text-[#6B8E6B] font-medium">Na: {afterCount}</span>}
          </div>
        )}
        {/* Upload-label selector */}
        <div className="ml-auto flex items-center gap-1 bg-[#F0EFEA] rounded-lg p-0.5" data-testid={`photo-upload-label-${zone.id}`}>
          <button
            onClick={() => setUploadLabel("before")}
            disabled={!quoteSaved}
            className={`px-3 h-7 rounded-md text-xs font-medium transition ${uploadLabel === "before" ? "bg-white text-[#2C2C2A] shadow" : "text-[#4F4F4D] hover:text-[#2C2C2A]"}`}
            data-testid={`photo-upload-label-before-${zone.id}`}
          >Voor</button>
          <button
            onClick={() => setUploadLabel("after")}
            disabled={!quoteSaved}
            className={`px-3 h-7 rounded-md text-xs font-medium transition ${uploadLabel === "after" ? "bg-white text-[#2C2C2A] shadow" : "text-[#4F4F4D] hover:text-[#2C2C2A]"}`}
            data-testid={`photo-upload-label-after-${zone.id}`}
          >Na</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-start">
        {photos.map((ph, idx) => (
          <div key={idx} className="relative group" data-testid={`zone-photo-${zone.id}-${idx}`}>
            <img src={ph.data_url} alt={`Zone ${idx + 1}`} className="w-20 h-20 object-cover rounded-lg border border-[#E5E2DC]" />
            {/* Voor/Na badge — clickable to toggle */}
            <button
              onClick={() => onSetLabel(idx, ph.label === "before" ? "after" : "before")}
              className={`absolute bottom-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded shadow-sm cursor-pointer hover:scale-105 transition ${ph.label === "after" ? "bg-[#6B8E6B] text-white" : "bg-[#D96C4A] text-white"}`}
              data-testid={`zone-photo-label-${zone.id}-${idx}`}
              title="Klik om Voor/Na te wisselen"
            >
              {ph.label === "after" ? "NA" : "VOOR"}
            </button>
            <button
              onClick={() => onRemove(idx)}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 hover:bg-red-500 hover:text-white flex items-center justify-center shadow"
              data-testid={`zone-photo-remove-${zone.id}-${idx}`}
              aria-label="Verwijder foto"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {!maxed && (
          <label
            onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
            className={`w-20 h-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer text-xs text-[#4F4F4D] transition ${drag ? "border-[#D96C4A] bg-[#FFF8F2]" : "border-[#C9C8C2] hover:border-[#D96C4A]"} ${!quoteSaved ? "opacity-40 cursor-not-allowed" : ""}`}
            data-testid={`zone-photo-upload-${zone.id}`}
          >
            <Camera className="w-5 h-5 mb-1" />
            <span className="text-[10px]">Upload {uploadLabel === "after" ? "(na)" : "(voor)"}</span>
            <input
              type="file" accept="image/*" multiple className="hidden"
              disabled={!quoteSaved}
              onChange={(e) => handleFiles(e.target.files)}
            />
          </label>
        )}
      </div>
    </div>
  );
}

// Compress an image File → base64 data URL (max width + quality).
async function compressImage(file, maxWidth = 1200, quality = 0.72) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}
